/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from 'react';
import OAuth2Login from 'react-simple-oauth2-login';
import { Button, Typography, Menu, MenuItem, IconButton, Box, ButtonProps } from '@mui/material';

import ConfirmationModal from '../ConfirmationModal';

import constants from './constants';
import useOAuthConnect from './useOAuthConnect';

import ConnectSVG from 'assets/img/icons/connect.svg';
import DotsVerticalIcon from 'modules/common/components/Icons/DotsVerticalIcon';
import CheckSmallIcon from 'modules/common/components/Icons/CheckSmallIcon';

interface DisconnectAffordanceProps {
    source: keyof typeof constants.oauthPresentation;
    /** Replaces the overflow menu with the caller's own trigger. */
    disconnectButton: React.ReactNode;
    onDisconnect: () => Promise<void>;
}

/** The confirm-to-disconnect action: the caller's trigger, or an overflow menu holding one. */
function DisconnectAffordance({ source, disconnectButton, onDisconnect }: Readonly<DisconnectAffordanceProps>) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const isOpen = Boolean(anchorEl);

    const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const onCloseMenu = () => {
        setAnchorEl(null);
    };

    if (disconnectButton) {
        return (
            <ConfirmationModal
                trigger={disconnectButton}
                title={constants.oauthPresentation[source].modalTitle}
                message={constants.oauthPresentation[source].modalMessage}
                yesText="Disconnect"
                noText="Go Back"
                callback={onDisconnect}
            />
        );
    }

    return (
        <>
            <IconButton
                aria-label="Disconnect"
                aria-controls={isOpen ? 'long-menu' : undefined}
                aria-expanded={isOpen ? 'true' : undefined}
                aria-haspopup="true"
                onClick={onOpenMenu}>
                <DotsVerticalIcon />
            </IconButton>
            <Menu data-testid="disconnect-dropdown" keepMounted anchorEl={anchorEl} open={isOpen} onClose={onCloseMenu}>
                <MenuItem onClick={onCloseMenu}>
                    <ConfirmationModal
                        trigger={
                            <Button size="small" color="secondary" variant="text">
                                Disconnect
                            </Button>
                        }
                        title={constants.oauthPresentation[source].modalTitle}
                        message={constants.oauthPresentation[source].modalMessage}
                        yesText="Disconnect"
                        noText="Go Back"
                        callback={onDisconnect}
                    />
                </MenuItem>
            </Menu>
        </>
    );
}

function AuthenticationPanelButton({
    source,
    showButtonIcon = true,
    buttonColor = 'primary',
    showConnectedConfirmation = true,
    disconnectButton = null,
    showOrganizationName = true,
}: {
    source: keyof typeof constants.oauthPresentation;
    showButtonIcon?: boolean;
    buttonColor?: ButtonProps['color'];
    showConnectedConfirmation?: boolean;
    disconnectButton?: React.ReactNode;
    showOrganizationName?: boolean;
}) {
    const {
        oAuthResult,
        deleteOAuthConnection,
        refetchoAuthResult,
        loginProps,
        isConnecting,
        refreshFailed,
        retryRefresh,
    } = useOAuthConnect(source);

    const connected = oAuthResult?.connected;
    const organizationName = oAuthResult?.organization_name;
    const showsOrganizationName = Boolean(organizationName) && showOrganizationName;

    // Not wrapped: this is a ConfirmationModal `callback`, and that component already
    // catches and reports a rejecting callback, and keeps itself open on failure.
    const handleDisconnect = async () => {
        await deleteOAuthConnection(source);
        await refetchoAuthResult();
    };

    if (connected) {
        return (
            <Box
                sx={{
                    ...(showsOrganizationName ? { marginTop: 3 } : {}),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-end',
                    }}>
                    {showConnectedConfirmation && (
                        <Button color="success" variant="outlined" size="small" startIcon={<CheckSmallIcon />}>
                            Connected
                        </Button>
                    )}
                    {source === 'gusto' && (
                        <DisconnectAffordance
                            source={source}
                            disconnectButton={disconnectButton}
                            onDisconnect={handleDisconnect}
                        />
                    )}
                </Box>
                {showsOrganizationName && (
                    <Typography variant="button" textAlign="right" marginRight={6} fontStyle="italic">
                        as {organizationName}
                    </Typography>
                )}
            </Box>
        );
    }

    if (refreshFailed) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color={buttonColor} loading={isConnecting} onClick={retryRefresh}>
                    Retry
                </Button>
            </Box>
        );
    }

    if (loginProps) {
        return (
            <OAuth2Login
                {...loginProps}
                render={({ onClick }) => {
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                }}>
                                <Button
                                    sx={{
                                        gap: 1,
                                    }}
                                    variant="contained"
                                    loading={isConnecting}
                                    color={buttonColor}
                                    onClick={onClick}
                                    startIcon={
                                        !isConnecting && showButtonIcon ? (
                                            <img src={ConnectSVG} alt={`connect_${source}`} />
                                        ) : undefined
                                    }>
                                    Connect
                                </Button>
                            </Box>
                        </Box>
                    );
                }}
            />
        );
    }

    return null;
}
export default AuthenticationPanelButton;
